import { atom, useAtomValue } from 'jotai';
import { atomFamily } from 'jotai-family';
import { selectAtom } from 'jotai/utils';
import { useMemo } from 'react';
import eq from 'fast-deep-equal';
import { GridGroup, GridRow, RowOrGroup } from '../types';
import type { PlanStrategy } from '../strategy/type';
import { EntityKey, gridAtom } from './grid';
import { baseRowAtom, baseGroupAtom } from './plan';
import { treeStructureAtom, visibleKeysAtom, collapsedGroupsAtom } from './tree';
import { usePlanStrategy } from './strategy';

function createGridRowsAtom(summary: PlanStrategy['summary']) {
  const gridRowAtom = atomFamily((key: EntityKey) => {
    const rowAtom = atom((get): RowOrGroup => {
      const { entities } = get(gridAtom);
      const structure = get(treeStructureAtom);
      const collapsed = get(collapsedGroupsAtom);

      const ref = entities[key];
      const meta = structure[key];
      const isExpanded = !collapsed.has(key);

      if (ref.type === 'ROW') {
        const row = get(baseRowAtom(ref.id));
        return {
          _isGroup: false,
          _key: key,
          depth: meta.depth,
          numChildren: meta.numChildren,
          isExpanded,
          ...row,
          isSelectable: true,
          groupDepth: meta.groupDepth,
          groupHeight: meta.groupHeight,
        } as GridRow;
      }

      const group = get(baseGroupAtom(ref.id));
      return {
        _isGroup: true,
        _key: key,
        depth: meta.depth,
        numChildren: meta.numChildren,
        isExpanded,
        ...group,
        summary: get(summary.group(key)),
        isSelectable: meta.hasDescendantRows,
        groupDepth: meta.groupDepth,
        groupHeight: meta.groupHeight,
      } as GridGroup;
    });
    return selectAtom(rowAtom, (v) => v, eq);
  });

  return atom<RowOrGroup[]>((get) => get(visibleKeysAtom).map((key) => get(gridRowAtom(key))));
}

export function useGridRows(): RowOrGroup[] {
  const { summary } = usePlanStrategy();
  const rowsAtom = useMemo(() => createGridRowsAtom(summary), [summary]);
  return useAtomValue(rowsAtom);
}
